import * as React from 'react';
import clsx from 'clsx';
import { Avatar } from '@/components/common/Avatar';
import { useAuth } from '@/hooks/useAuth';
import { WaveformPlayer } from '@/components/audio/WaveformPlayer';
import greenMicrophoneIcon from '@/assets/images/svg/green_microphone_icon.svg';
import { getUserInitials } from '@/utils/formatters';

interface UserAudioBubbleProps {
  audioUrl: string;
  waveform?: number[];
  duration?: number;
}

/**
 * UserAudioBubble Component
 * Displays an audio player bubble for messages sent by the current user.
 * Uses Wavesurfer.js for visualization and playback.
 */
export const UserAudioBubble: React.FC<UserAudioBubbleProps> = ({ audioUrl }) => {
  const { user } = useAuth();
  const initials = user ? getUserInitials(user) : 'ME';

  return (
    <div className='flex items-end justify-end'>
      <div
        className={clsx(
          'relative flex min-w-[300px] max-w-[420px] items-center gap-4',
          'rounded-24',
          'bg-user-bubble-gradient p-3 pr-6 text-white shadow-md'
        )}
      >
        {/* Avatar + Mic badge */}
        <div className='relative shrink-0'>
          <Avatar
            src={user?.profile_picture_url ?? null}
            initials={initials}
            size={48}
            className='border-2 border-brand-200'
          />
          <img
            src={greenMicrophoneIcon}
            alt='Voice Message'
            className='absolute -bottom-1 -right-1 h-[17px] w-[11.77px]'
          />
        </div>

        {/* Wavesurfer Player */}
        <div className='min-w-0 flex-1'>
          <WaveformPlayer
            url={audioUrl}
            height={30}
            barWidth={3}
            barGap={2}
            barRadius={3}
            waveColor='#FFFFFF'
            progressColor='#6EDF21'
            buttonClassName='text-accent-300 hover:text-white transition-colors'
            // Absolute positioning for the duration text to bottom-right
            className='relative pb-2'
            textClassName='absolute -bottom-1 -right-2 font-sans text-10 font-medium text-white/70'
          />
        </div>
      </div>
    </div>
  );
};
